// Utilidades puras de la integracion con Tilopay (SDK JavaScript V2). Sin React ni red, para poder probarlas.

export const TILOPAY_SDK_URL = process.env.REACT_APP_TILOPAY_SDK_URL || "https://app.tilopay.com/sdk/v2/sdk_tpay.min.js";
export const TILOPAY_PENDING_KEY = "tilopayPending";

const normalize = (value) => String(value ?? "")
  .normalize("NFD")
  .replace(/[̀-ͯ]/g, "")
  .toLowerCase()
  .trim();

export const isTilopayPayment = (payment = {}) => (
  normalize(payment?.payment).includes("tilopay") ||
  normalize(payment?.integration?.name ?? payment?.integration_name).includes("tilopay")
);

// Pagina del iframe que contiene el formulario del SDK (public/tilopay-frame.html). El SDK corre ahi dentro para que
// el redirect final no saque al cliente del checkout.
export const getFrameUrl = () => `${process.env.PUBLIC_URL || ""}/tilopay-frame.html`;

// El SDK redirige a esta URL al terminar, DENTRO del iframe; la pagina avisa del resultado al checkout.
// Por defecto es el backend de la app (REACT_APP_API_URL absoluta, p. ej. localhost:8080 en desarrollo); si la API se
// sirve en el mismo dominio (/api proxeado) se usa ese dominio. Se puede forzar con REACT_APP_TILOPAY_RETURN_URL.
export const getReturnUrl = () => {
  if (process.env.REACT_APP_TILOPAY_RETURN_URL) return process.env.REACT_APP_TILOPAY_RETURN_URL;
  const api = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const base = /^https?:\/\//i.test(api) ? api : `${window.location.origin}${api || "/api"}`;
  return `${base}/tilopay/return/embedded`;
};

export const splitName = (fullName = "") => {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  return { first: parts[0] || "", last: parts.slice(1).join(" ") };
};

export const encodeReturnData = (data) => {
  try {
    return window.btoa(unescape(encodeURIComponent(JSON.stringify(data))));
  } catch (err) {
    return "";
  }
};

const compact = (obj) => Object.fromEntries(
  Object.entries(obj).filter(([, value]) => value !== undefined && value !== null && value !== "")
);

// Valores de respaldo para los datos de facturacion: el SDK/API de Tilopay exige direccion, ciudad, estado, codigo
// postal y telefono aunque el pedido sea para recoger en tienda. Se usan los reales cuando existen; configurables por entorno.
const BILLING_DEFAULTS = {
  address: process.env.REACT_APP_TILOPAY_DEFAULT_ADDRESS || "Panama",
  city: process.env.REACT_APP_TILOPAY_DEFAULT_CITY || "Panama",
  state: process.env.REACT_APP_TILOPAY_DEFAULT_STATE || "PA-8",
  zip: process.env.REACT_APP_TILOPAY_DEFAULT_ZIP || "0000",
  phone: "00000000",
};

const clip = (value, max) => String(value || "").trim().slice(0, max);

// Parametros de Tilopay.Init(). Los datos de pago (token, monto, moneda, orden) vienen del servidor.
export const buildInitParams = ({ session, payer = {}, returnUrl }) => {
  const { first, last } = splitName(payer.name);
  const firstName = first || "Cliente";
  const address = clip(payer.address, 100) || BILLING_DEFAULTS.address;
  return compact({
    token: session.token,
    currency: session.currency,
    language: session.language || "es",
    amount: Number(session.amount),
    orderNumber: session.order_number,
    capture: 1,
    subscription: 0,
    hashVersion: "V2",
    redirect: returnUrl,
    returnData: encodeReturnData({ order_number: session.order_number }),
    billToEmail: payer.email,
    billToFirstName: firstName,
    billToLastName: last || firstName,
    billToTelephone: String(payer.phone || "").replace(/[^\d+]/g, "") || BILLING_DEFAULTS.phone,
    billToAddress: address,
    billToAddress2: clip(payer.address2, 100) || "N/A",
    billToCity: clip(payer.city, 50) || BILLING_DEFAULTS.city,
    billToState: clip(payer.state, 10) || BILLING_DEFAULTS.state,
    billToZipPostCode: clip(payer.zip, 12) || BILLING_DEFAULTS.zip,
    billToCountry: process.env.REACT_APP_TILOPAY_COUNTRY || "PA",
  });
};

export const isValidEmail = (value = "") => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value).trim());

// Tarjetas guardadas: Tilopay las asocia al correo, asi que solo se muestran u ofrecen cuando el servidor lo habilita
// (caja con TILOPAY_SAVE_CARDS y pedido de un cliente con cuenta) y el correo del pago es el de la cuenta del cliente.
// Para invitados o con otro correo no se muestran, aunque Tilopay las devuelva.
export const canUseSavedCards = (session, payer = {}) => {
  const account = String(payer.accountEmail || "").trim().toLowerCase();
  return session?.save_cards === true && isValidEmail(account) && account === String(payer.email || "").trim().toLowerCase();
};

// Resultado de Tilopay.Init(): devuelve null si todo esta bien o el motivo por el que no se puede cobrar.
export const validateInitResult = (init, session) => {
  if (!init || typeof init !== "object") return "No se pudo iniciar el formulario de pago.";
  if (init.message && init.message !== "Success") return init.message;
  // El host es el mismo para pruebas y produccion: el entorno real solo se sabe aqui.
  if (init.environment && session?.environment_expected && String(init.environment).toUpperCase() !== String(session.environment_expected).toUpperCase()) {
    return "El entorno de pagos no coincide con la configuracion de la tienda. Contacta al comercio.";
  }
  const cards = Array.isArray(init.methods) ? init.methods.filter((method) => method.type === "card") : [];
  if (cards.length === 0) return "El pago con tarjeta no esta disponible en este momento.";
  return null;
};

// Estados que entrega GET /tilopay/status (ver backend: tilopayPayments.js).
export const PAYMENT_PENDING_STATUSES = ["created", "approved_unlinked", "registering"];

export const getStatusCopy = (status) => {
  switch (status) {
    case "registered":
      return { tone: "success", title: "Pago aprobado", text: "Tu pedido fue enviado para aprobacion." };
    case "declined":
      return { tone: "danger", title: "Pago rechazado", text: "Tu tarjeta no fue aprobada. No se realizo ningun cobro. Tu carrito sigue guardado: intenta con otra tarjeta." };
    case "abandoned":
      return { tone: "warning", title: "Pago no completado", text: "No recibimos la confirmacion del pago. Si no se te cobro nada, puedes intentar de nuevo desde tu carrito." };
    case "mismatch":
    case "duplicate_approved":
      return { tone: "warning", title: "Estamos revisando tu pago", text: "Recibimos tu pago pero necesitamos validarlo manualmente. No lo repitas; el comercio se pondra en contacto." };
    case "approved_unlinked":
      return { tone: "info", title: "Pago recibido", text: "Estamos registrando tu pedido. No cierres esta pagina." };
    default:
      return { tone: "info", title: "Confirmando tu pago", text: "Esto puede tardar unos segundos. No cierres esta pagina." };
  }
};

export const readPending = () => {
  try {
    return JSON.parse(window.localStorage.getItem(TILOPAY_PENDING_KEY) || "null");
  } catch (err) {
    return null;
  }
};

export const writePending = (value) => {
  try {
    if (value) window.localStorage.setItem(TILOPAY_PENDING_KEY, JSON.stringify(value));
    else window.localStorage.removeItem(TILOPAY_PENDING_KEY);
  } catch (err) { /* almacenamiento no disponible: el resultado igual se confirma por el servidor */ }
};

// Mismas llaves que Checkout.clearCheckoutPaymentStorage al terminar un pago con exito.
export const clearCheckoutStorageAfterPayment = () => {
  try {
    ["shoppingCart", "shoppingCartCombos", "invoiceData", "event_stand_name", "event_stand_number", "orderNumber", TILOPAY_PENDING_KEY]
      .forEach((key) => window.localStorage.removeItem(key));
    window.sessionStorage.removeItem("checkoutCalculationSessionId");
  } catch (err) { /* nada que limpiar */ }
};
